import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@sw/api-client';
import {
  createTalentComment,
  deleteTalentComment,
  listTalentComments,
  updateTalentComment,
  type CreateCommentParams,
  type ListCommentsParams,
  type ListCommentsResponse,
  type UpdateCommentParams,
} from './talentCommentsApi';

export const TALENT_COMMENTS_QUERY_KEY = ['talent', 'comments'] as const;

export function useTalentComments(params: ListCommentsParams) {
  return useQuery<ListCommentsResponse>({
    queryKey: [
      ...TALENT_COMMENTS_QUERY_KEY,
      params.shortlistPublicKey,
      params.talentPublicKey,
      params.pageNumber ?? 1,
      params.pageSize ?? 20,
    ],
    queryFn: () => listTalentComments(params),
    staleTime: 15_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 404 || error.status === 403)) return false;
      return failureCount < 2;
    },
  });
}

export function useCreateTalentComment(params: Pick<ListCommentsParams, 'shortlistPublicKey' | 'talentPublicKey'>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Pick<CreateCommentParams, 'text'>) =>
      createTalentComment({
        shortlistPublicKey: params.shortlistPublicKey,
        talentPublicKey: params.talentPublicKey,
        text: input.text,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: [
          ...TALENT_COMMENTS_QUERY_KEY,
          params.shortlistPublicKey,
          params.talentPublicKey,
        ],
      });
    },
  });
}

export function useUpdateTalentComment(params: Pick<ListCommentsParams, 'shortlistPublicKey' | 'talentPublicKey'>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCommentParams) => updateTalentComment(input),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: [
          ...TALENT_COMMENTS_QUERY_KEY,
          params.shortlistPublicKey,
          params.talentPublicKey,
        ],
      });
    },
  });
}

export function useDeleteTalentComment(params: Pick<ListCommentsParams, 'shortlistPublicKey' | 'talentPublicKey'>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentPublicKey: string) => deleteTalentComment({ commentPublicKey }),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: [
          ...TALENT_COMMENTS_QUERY_KEY,
          params.shortlistPublicKey,
          params.talentPublicKey,
        ],
      });
    },
  });
}
